// Client of the API's transparent-authentication endpoints (the app's own sign-in, invitation and
// password screens). Deliberately bare fetch(), never the app's axios instance: the global axios
// interceptors stamp a bearer on every call and tear the session down on 401 — neither is wanted
// for requests a signed-out person makes.
import { getDefaultRegion, Region } from '../regionBootstrap';
import { authApiBase } from './authMode';

export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: number;
  orgId: string;
  status: string;
}

/**
 * What the legacy app used to decode from token claims (added by Auth0 Actions on a redirect
 * sign-in). The legacy API hands it over next to the tokens of a transparent session.
 */
export interface SessionProfile {
  role: number | null;
  organization: {
    id: string;
    name: string;
    display_name: string;
    metadata: { location: string; orgType: string };
  } | null;
  user_metadata: {
    communicationId: string;
    ftue: boolean;
    name: string;
    headshot: string;
    coId: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface SessionPayload {
  accessToken: string;
  idToken: string;
  refreshToken: string | null;
  /** seconds until the access token expires */
  expiresIn: number;
  tokenType: string;
  user: SessionUser;
  /** legacy API only */
  profile?: SessionProfile;
}

export interface InvitationPreview {
  email: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  organizationLogo: string | null;
  programName: string | null;
  role: string;
  expiresAt: string;
  /** the address already has a Dosen password: the form asks for it instead of a new one */
  accountExists: boolean;
  /** e-mail and password only: no identity provider is offered (a Hiring candidate's invitation) */
  passwordOnly?: boolean;
}

/** The link in the e-mail (`token`), or the id of an invitation issued through Auth0 earlier (`ticket`). */
export interface InvitationRef {
  token?: string;
  ticket?: string;
}

/** One way the signed-in account signs in (settings screen). */
export interface SignInMethod {
  provider: string;
  connection: string;
  userId: string;
  label: string;
  email: string | null;
  /** the identity the account is made of: cannot be disconnected */
  main: boolean;
  removable: boolean;
}

/**
 * none: no password identity yet · unset: a password nobody knows (made for a provider sign-in) ·
 * set: a password the person chose · elsewhere: a password account of its own exists for the address
 */
export type PasswordState = 'none' | 'unset' | 'set' | 'elsewhere';

export interface SignInMethods {
  methods: SignInMethod[];
  passwordState: PasswordState;
  hasPassword: boolean;
  /** until when sign-in methods may be changed without re-authenticating (null: confirm first) */
  reauthUntil: string | null;
}

/** What an identity-provider transaction is for (see sso.ts). */
export type SsoPurpose = 'signin' | 'link' | 'reauth';

/** The e-mail step of the sign-in screen: what the API offers for an address. */
export interface SignInOptions {
  /** offer the e-mail + password form */
  password: boolean;
  /** "Continue with …" buttons, in the deployment's order */
  providers: Array<{ connection: string; label: string }>;
}

export interface PublicOrganization {
  id: string;
  slug: string;
  name: string;
  logo: string | null;
  auth0OrgId: string | null;
  /** legacy API only: the region that holds the organization */
  location?: string;
}

/**
 * A refusal from the API. `code` is stable (the screens switch on it); `message` is already written
 * for the person and can be shown as is.
 */
export class AuthApiError extends Error {
  public readonly code: string;

  public readonly status: number;

  public readonly retryAfterSeconds?: number;

  /** nothing was changed and the same request may simply be sent again */
  public readonly retryable: boolean;

  /** the other fields the refusal carried (e.g. `handoff` and `method` of `link_required`) */
  public readonly extra: Record<string, unknown>;

  public constructor(
    code: string,
    status: number,
    message: string,
    extra: { retryAfterSeconds?: number; retryable?: boolean; [key: string]: unknown } = {}
  ) {
    super(message);
    this.name = 'AuthApiError';
    this.code = code;
    this.status = status;
    this.retryAfterSeconds = extra.retryAfterSeconds;
    this.retryable = extra.retryable === true;
    this.extra = extra;
    Object.setPrototypeOf(this, AuthApiError.prototype); // `instanceof` across the ES5 class transform
  }
}

export const isAuthApiError = (err: unknown): err is AuthApiError => err instanceof AuthApiError;

// Consumption-plan cold starts can take a while; the screens show progress meanwhile.
const TIMEOUT_MS = 25000;

interface CallOptions {
  /** regional API to use (links carry it; sign-in resolves it from the address) */
  region?: Region | null;
  accessToken?: string;
  /** the legacy functions identify the caller by both tokens */
  idToken?: string;
  /** let the request outlive the page (sign-out) */
  keepalive?: boolean;
}

async function call<T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body?: unknown,
  options: CallOptions = {}
): Promise<T> {
  const region = options.region ?? getDefaultRegion();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`${authApiBase()}${path}`, {
      method,
      signal: controller.signal,
      keepalive: options.keepalive,
      headers: {
        accept: 'application/json',
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(region ? { location: region } : {}),
        ...(options.accessToken ? { authorization: `Bearer ${options.accessToken}` } : {}),
        ...(options.idToken ? { idtoken: options.idToken } : {})
      },
      body: body !== undefined ? JSON.stringify(body) : undefined
    });
  } catch {
    throw new AuthApiError('network_error', 0, "We couldn't reach Dosen. Check your connection and try again.", {
      retryable: true
    });
  } finally {
    clearTimeout(timer);
  }

  const text = await res.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    /* not JSON (a proxy error page) — handled below */
  }
  if (res.ok) return json as T;
  const {
    error: _error,
    code: _code,
    message: _message,
    ...carried
  } = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
  throw new AuthApiError(
    typeof json?.code === 'string' ? json.code : res.status >= 500 ? 'service_unavailable' : 'request_failed',
    res.status,
    typeof json?.message === 'string' && json.message
      ? json.message
      : 'Something went wrong on our side. Nothing was changed — try again in a moment.',
    {
      ...carried,
      retryAfterSeconds: typeof json?.retryAfterSeconds === 'number' ? json.retryAfterSeconds : undefined,
      retryable: json?.retryable === true || res.status >= 500
    }
  );
}

/** The tokens a signed-in call identifies itself with (the legacy functions want both). */
export interface CallerTokens {
  accessToken: string;
  idToken: string;
  region?: Region | null;
}

export const authApi = {
  login: (input: { email: string; password: string }, region?: Region | null) =>
    call<SessionPayload>('POST', '/login', input, { region }),

  /**
   * `subject`: whose session this is (the `sub` of the stored tokens). An API that does not hold
   * that account answers `account_not_provisioned` WITHOUT spending the refresh token — the session
   * may belong to the web app's other half. `region`: the regional API that opened the session.
   */
  refresh: (refreshToken: string, subject?: string, region?: Region | null) =>
    call<SessionPayload>('POST', '/refresh', { refreshToken, ...(subject ? { subject } : {}) }, { region }),

  logout: (refreshToken: string, region?: Region | null) =>
    call<null>('POST', '/logout', { refreshToken }, { keepalive: true, region }),

  forgotPassword: (email: string, region?: Region | null) =>
    call<unknown>('POST', '/password/forgot', { email }, { region }),

  describeResetLink: (token: string, region?: Region | null) =>
    call<{ email: string }>('POST', '/password/reset/check', { token }, { region }),

  resetPassword: (input: { token: string; password: string }, region?: Region | null) =>
    call<null>('POST', '/password/reset', input, { region }),

  changePassword: (
    input: { currentPassword: string; newPassword: string },
    accessToken: string,
    idToken?: string,
    region?: Region | null
  ) => call<{ session: SessionPayload | null }>('POST', '/password/change', input, { accessToken, idToken, region }),

  /** Legacy API: the profile that belongs to a session opened elsewhere (the new app shares storage). */
  profile: (accessToken: string, idToken: string) =>
    call<{ profile: SessionProfile }>('GET', '/profile', undefined, { accessToken, idToken }),

  previewInvitation: (ref: InvitationRef, region?: Region | null) =>
    call<InvitationPreview>('POST', '/invitations/preview', ref, { region }),

  acceptInvitation: (
    input: InvitationRef & { password: string; firstName?: string; lastName?: string },
    region?: Region | null
  ) => call<{ user: SessionUser; session: SessionPayload | null }>('POST', '/invitations/accept', input, { region }),

  organization: (slug: string) => call<PublicOrganization>('GET', `/organizations/${encodeURIComponent(slug)}`),

  /** Which ways in to offer for an address (`organization`: the sign-in page's `?org=` slug). */
  signInOptions: (input: { email: string; organization?: string }) =>
    call<SignInOptions>('POST', '/sign-in-options', input),

  /**
   * Starts a transaction at an identity provider: the API answers with the address the browser must
   * open (the provider's own page). `challenge` is the hash of a secret this tab keeps — see sso.ts.
   * `purpose` link / reauth are for the signed-in person (`caller`): the transaction is theirs alone.
   */
  startSso: (
    input: { connection: string; returnTo: string; challenge: string; purpose?: SsoPurpose },
    caller?: CallerTokens
  ) => call<{ url: string }>('POST', '/sso/start', input, caller ?? {}),

  /**
   * Trades the one-time code the provider flow came back with, and the secret behind the
   * challenge, for the session. With an invitation reference the sign-in accepts that invitation.
   */
  completeSso: (input: { code: string; verifier: string } & InvitationRef) =>
    call<SessionPayload>('POST', '/sso/complete', input),

  /** Finishes a "link" transaction: the provider identity is connected to the signed-in account. */
  completeSsoLink: (input: { code: string; verifier: string }, caller: CallerTokens) =>
    call<SignInMethods>('POST', '/sso/complete', { ...input, purpose: 'link' }, caller),

  /** Finishes a "reauth" transaction: the provider signed the person in again. */
  completeSsoReauth: (input: { code: string; verifier: string }, caller: CallerTokens) =>
    call<{ reauthUntil: string }>('POST', '/sso/complete', { ...input, purpose: 'reauth' }, caller),

  /**
   * Answers a `link_required` refusal of a provider sign-in: the account's password connects the
   * provider identity and opens the account's session.
   */
  confirmLink: (input: { handoff: string; password: string }, region?: Region | null) =>
    call<SessionPayload>('POST', '/link/confirm', input, { region }),

  // ---- sign-in methods (settings screen) ----

  signInMethods: (caller: CallerTokens) => call<SignInMethods>('GET', '/sign-in-methods', undefined, caller),

  /** Chooses the password of an account that has none the person knows (step-up required). */
  setPassword: (input: { newPassword: string }, caller: CallerTokens) =>
    call<{ session: SessionPayload | null }>('POST', '/password/set', input, caller),

  /** Connects a password account of its own that exists for the address (its password proves it). */
  connectPassword: (input: { password: string }, caller: CallerTokens) =>
    call<SignInMethods>('POST', '/password/connect', input, caller),

  /** Password re-authentication: opens the window in which sign-in methods may be changed. */
  reauthenticate: (input: { password: string }, caller: CallerTokens) =>
    call<{ reauthUntil: string }>('POST', '/reauth', input, caller),

  /** Disconnects a provider identity (step-up required). */
  unlink: (input: { provider: string; userId: string }, caller: CallerTokens) =>
    call<SignInMethods>(
      'DELETE',
      `/link/${encodeURIComponent(input.provider)}/${encodeURIComponent(input.userId)}`,
      undefined,
      caller
    )
};
